import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Grid, Chip, Button, Stack,
  CircularProgress, Snackbar, Alert, Divider, IconButton, Tooltip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import PetsIcon from '@mui/icons-material/Pets';
import HotelIcon from '@mui/icons-material/Hotel';
import {
  collection, getDocs, doc, updateDoc, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';

import { COLORS } from '../theme/constants';
import {
  getRequestStatusConfig, getPriorityConfig, PREOP_EXAMS,
} from '../models/surgeryModel';

const { primary: PRIMARY, teal: TEAL, danger: DANGER } = COLORS;

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function SurgeryWorklist() {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { filterByBranch } = useBranch();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [rejectDlg, setRejectDlg] = useState({ open: false, request: null, reason: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'surgeryRequests'));
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list = filterByBranch(list, { includeUnassigned: true });
      list.sort((a, b) => {
        const order = { Emergencia: 0, Urgente: 1, Electiva: 2 };
        const pa = order[a.priority] ?? 3;
        const pb = order[b.priority] ?? 3;
        if (pa !== pb) return pa - pb;
        const da = a.createdAt?.toDate ? a.createdAt.toDate() : 0;
        const dbb = b.createdAt?.toDate ? b.createdAt.toDate() : 0;
        return dbb - da;
      });
      setRequests(list);
    } catch (err) {
      console.error('Error cargando solicitudes de cirugía:', err);
      setSnackbar({ open: true, message: 'Error al cargar la lista de trabajo', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterByBranch]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const counts = useMemo(() => {
    const c = { pending: 0, scheduled: 0, completed: 0, rejected: 0, cancelled: 0 };
    requests.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [requests]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return requests.filter((r) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (priorityFilter && r.priority !== priorityFilter) return false;
      if (q && !(
        (r.patientName || '').toLowerCase().includes(q) ||
        (r.proposedProcedure || '').toLowerCase().includes(q) ||
        (r.ownerName || '').toLowerCase().includes(q) ||
        (r.requestingVetName || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [requests, statusFilter, priorityFilter, search]);

  const handleSchedule = (req) => {
    navigate('/surgery-scheduler', { state: { surgeryRequest: req } });
  };

  const handleHospitalize = (req) => {
    if (!req.patientId) {
      setSnackbar({ open: true, message: 'La derivación no tiene paciente vinculado', severity: 'warning' });
      return;
    }
    navigate(`/hospital?patientId=${req.patientId}&newOrder=true`);
  };

  const handleReject = async () => {
    const req = rejectDlg.request;
    if (!req) return;
    try {
      await updateDoc(doc(db, 'surgeryRequests', req.id), {
        status: 'rejected',
        rejectionReason: rejectDlg.reason || '',
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'surgeryRequests', req.id, 'auditLog'), {
        action: 'request_rejected',
        details: `Derivación rechazada. Motivo: ${rejectDlg.reason || 'Sin especificar'}`,
        performedBy: currentUser?.uid || '',
        performedByName: userData?.name || currentUser?.email || '',
        timestamp: serverTimestamp(),
      });
      setSnackbar({ open: true, message: 'Derivación rechazada', severity: 'success' });
      setRejectDlg({ open: false, request: null, reason: '' });
      loadRequests();
    } catch (err) {
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    }
  };

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <LocalHospitalIcon sx={{ color: PRIMARY, fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color={PRIMARY}>Lista de Trabajo — Cirugías</Typography>
          <Typography variant="caption" color="text.secondary">
            Derivaciones pendientes de programación
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRequests}>Actualizar</Button>
        <Button variant="contained" startIcon={<EventAvailableIcon />} onClick={() => navigate('/surgery-scheduler')}
          sx={{ bgcolor: PRIMARY, fontWeight: 700, '&:hover': { bgcolor: '#122a47' } }}>
          Ir al calendario
        </Button>
      </Box>

      {/* KPIs */}
      <Stack direction="row" spacing={2} sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {[
          { label: 'Pendientes', value: counts.pending, color: '#F59E0B' },
          { label: 'Agendadas', value: counts.scheduled, color: '#3B82F6' },
          { label: 'Completadas', value: counts.completed, color: '#10B981' },
          { label: 'Rechazadas', value: counts.rejected, color: DANGER },
        ].map(({ label, value, color }) => (
          <Paper key={label} elevation={0} sx={{ px: 2, py: 1, border: '1px solid #E2E8F0', borderRadius: 2, minWidth: 120 }}>
            <Typography variant="h5" fontWeight={800} color={color}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* Filtros */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup size="small" exclusive value={statusFilter}
            onChange={(_, v) => setStatusFilter(v)}>
            <ToggleButton value="pending">Pendientes</ToggleButton>
            <ToggleButton value="scheduled">Agendadas</ToggleButton>
            <ToggleButton value="completed">Completadas</ToggleButton>
            <ToggleButton value="rejected">Rechazadas</ToggleButton>
            <ToggleButton value="">Todas</ToggleButton>
          </ToggleButtonGroup>
          <TextField select size="small" label="Prioridad" value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="Emergencia">Emergencia</MenuItem>
            <MenuItem value="Urgente">Urgente</MenuItem>
            <MenuItem value="Electiva">Electiva</MenuItem>
          </TextField>
          <TextField size="small" label="Buscar" placeholder="Paciente, procedimiento, tutor…"
            value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, borderRadius: 3, border: '1px dashed #CBD5E1', textAlign: 'center' }}>
          <LocalHospitalIcon sx={{ fontSize: 56, color: '#CBD5E1' }} />
          <Typography color="text.disabled" mt={1}>No hay derivaciones con estos filtros.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((r) => {
            const stCfg = getRequestStatusConfig(r.status);
            const prCfg = getPriorityConfig(r.priority);
            return (
              <Grid key={r.id} size={{ xs: 12, md: 6, lg: 4 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0',
                  borderLeft: `4px solid ${prCfg.color}`, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar sx={{ bgcolor: TEAL, width: 36, height: 36 }}><PetsIcon fontSize="small" /></Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>{r.patientName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {r.patientSpecies} {r.patientBreed ? `· ${r.patientBreed}` : ''}
                      </Typography>
                    </Box>
                    <Chip label={prCfg.label} size="small" sx={{ bgcolor: prCfg.bg, color: prCfg.color, fontWeight: 700, fontSize: '0.65rem' }} />
                  </Box>

                  <Typography variant="body2" fontWeight={700} color={PRIMARY} sx={{ mb: 0.3 }}>
                    {r.proposedProcedure || 'Procedimiento sin especificar'}
                  </Typography>
                  {r.clinicalDiagnosis && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Dx: {r.clinicalDiagnosis}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
                    <Chip label={stCfg.label} size="small" sx={{ bgcolor: stCfg.bg, color: stCfg.color, fontSize: '0.65rem' }} />
                    {r.asaScore && <Chip label={`ASA ${r.asaScore}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                    {r.estimatedDurationMin && <Chip label={`${r.estimatedDurationMin} min`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Deriva: {r.requestingVetName || '—'} · {fmtDate(r.createdAt)}
                  </Typography>

                  <Divider sx={{ my: 1.2 }} />
                  <Box sx={{ mt: 'auto', display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Tooltip title="Ver detalle">
                      <IconButton size="small" onClick={() => setDetail(r)}><VisibilityIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {r.patientId && (
                      <Button size="small" variant="outlined" startIcon={<HotelIcon />}
                        onClick={() => handleHospitalize(r)}
                        sx={{ color: '#1D4ED8', borderColor: '#BFDBFE', fontWeight: 700, fontSize: '0.7rem' }}>
                        Hospitalizar
                      </Button>
                    )}
                    {r.presupuestoId && (
                      <Button size="small" variant="outlined"
                        onClick={() => navigate(`/presupuestos/${r.presupuestoId}`)}>
                        Ver presupuesto
                      </Button>
                    )}
                    {r.status === 'pending' && (
                      <>
                        <Tooltip title="Rechazar">
                          <IconButton size="small" onClick={() => setRejectDlg({ open: true, request: r, reason: '' })}>
                            <BlockIcon fontSize="small" sx={{ color: DANGER }} />
                          </IconButton>
                        </Tooltip>
                        <Button size="small" variant="contained" startIcon={<EventAvailableIcon />}
                          onClick={() => handleSchedule(r)}
                          sx={{ bgcolor: PRIMARY, fontWeight: 700, '&:hover': { bgcolor: '#122a47' } }}>
                          Programar
                        </Button>
                      </>
                    )}
                    {r.status === 'scheduled' && (
                      <Button size="small" variant="outlined"
                        onClick={() => navigate('/surgery-scheduler')}>
                        Ver en calendario
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Detalle */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>
              Derivación — {detail.patientName}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={1.5}>
                <Grid size={12}>
                  <Typography variant="subtitle2" fontWeight={700} color={PRIMARY}>{detail.proposedProcedure}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detail.procedureCategory} · Prioridad {detail.priority}
                    {detail.laterality ? ` · ${detail.laterality}` : ''}
                  </Typography>
                </Grid>
                <Grid size={12}><Divider /></Grid>
                <Grid size={6}><Typography variant="caption" color="text.secondary">Paciente</Typography>
                  <Typography variant="body2">{detail.patientName} ({detail.patientSpecies})</Typography></Grid>
                <Grid size={6}><Typography variant="caption" color="text.secondary">Peso</Typography>
                  <Typography variant="body2">{detail.patientWeight ? `${detail.patientWeight} kg` : '—'}</Typography></Grid>
                <Grid size={6}><Typography variant="caption" color="text.secondary">Tutor</Typography>
                  <Typography variant="body2">{detail.ownerName || '—'} {detail.ownerPhone ? `· ${detail.ownerPhone}` : ''}</Typography></Grid>
                <Grid size={6}><Typography variant="caption" color="text.secondary">ASA / Duración</Typography>
                  <Typography variant="body2">{detail.asaScore ? `ASA ${detail.asaScore}` : '—'} · {detail.estimatedDurationMin || '—'} min</Typography></Grid>
                <Grid size={12}><Divider /></Grid>
                <Grid size={12}><Typography variant="caption" color="text.secondary">Diagnóstico</Typography>
                  <Typography variant="body2">{detail.clinicalDiagnosis || '—'}</Typography></Grid>
                <Grid size={12}><Typography variant="caption" color="text.secondary">Indicación quirúrgica</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detail.indication || '—'}</Typography></Grid>
                {detail.clinicalSummary && (
                  <Grid size={12}><Typography variant="caption" color="text.secondary">Resumen clínico</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detail.clinicalSummary}</Typography></Grid>
                )}
                {detail.comorbidities && (
                  <Grid size={12}><Typography variant="caption" color="text.secondary">Comorbilidades / riesgos</Typography>
                    <Typography variant="body2">{detail.comorbidities}</Typography></Grid>
                )}
                {detail.currentMedications && (
                  <Grid size={12}><Typography variant="caption" color="text.secondary">Medicación actual</Typography>
                    <Typography variant="body2">{detail.currentMedications}</Typography></Grid>
                )}
                {detail.allergies && (
                  <Grid size={12}><Chip label={`⚠ Alergias: ${detail.allergies}`} size="small"
                    sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 700 }} /></Grid>
                )}
                <Grid size={12}><Typography variant="caption" color="text.secondary">Exámenes preoperatorios solicitados</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {PREOP_EXAMS.filter((ex) => detail.preOpExams?.[ex.key]).map((ex) => (
                      <Chip key={ex.key} label={ex.label} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    ))}
                    {PREOP_EXAMS.every((ex) => !detail.preOpExams?.[ex.key]) && (
                      <Typography variant="body2">Ninguno</Typography>
                    )}
                  </Box>
                </Grid>
                {(detail.preferredDateFrom || detail.preferredDateTo) && (
                  <Grid size={12}><Typography variant="caption" color="text.secondary">Fecha preferida</Typography>
                    <Typography variant="body2">{detail.preferredDateFrom || '—'} a {detail.preferredDateTo || '—'}</Typography></Grid>
                )}
                {detail.specialRequirements && (
                  <Grid size={12}><Typography variant="caption" color="text.secondary">Requerimientos especiales</Typography>
                    <Typography variant="body2">{detail.specialRequirements}</Typography></Grid>
                )}
                {detail.notes && (
                  <Grid size={12}><Typography variant="caption" color="text.secondary">Notas al coordinador</Typography>
                    <Typography variant="body2">{detail.notes}</Typography></Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetail(null)}>Cerrar</Button>
              {detail.patientId && (
                <Button variant="outlined" startIcon={<HotelIcon />}
                  onClick={() => { const r = detail; setDetail(null); handleHospitalize(r); }}
                  sx={{ color: '#1D4ED8', borderColor: '#BFDBFE', fontWeight: 700 }}>
                  Hospitalizar
                </Button>
              )}
              {detail.status === 'pending' && (
                <Button variant="contained" startIcon={<EventAvailableIcon />}
                  onClick={() => { const r = detail; setDetail(null); handleSchedule(r); }}
                  sx={{ bgcolor: PRIMARY, fontWeight: 700, '&:hover': { bgcolor: '#122a47' } }}>
                  Programar cirugía
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Rechazo */}
      <Dialog open={rejectDlg.open} onClose={() => setRejectDlg({ open: false, request: null, reason: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Rechazar derivación</DialogTitle>
        <DialogContent dividers>
          <TextField autoFocus label="Motivo del rechazo" fullWidth multiline rows={3} size="small"
            value={rejectDlg.reason} onChange={(e) => setRejectDlg((p) => ({ ...p, reason: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDlg({ open: false, request: null, reason: '' })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Rechazar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
